import * as React from 'react';
import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';

import { Box, Container, Grid, Tabs, Tab, Paper, ThemeProvider, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';

import UserDetailsCard from './UserDetailsCard';
import RequestedContactsList from './RequestedContact';
// import UserSubscriptionDetails from './UserSubscriptionDetails';
import UserBillingDetails from './UserBillingDetails';
import UserInvoicesList from './UserInvoicesList';
import ChangePasswordComponent from './ChangePassword';
import usersEndpoints from '../../../services/userServices';
import {
    theme, PAGE_BG, CONTAINER_SX, CARD_SX, PRIMARY_BTN_SX,
    BRAND, MUTED, TEXT, DIVIDER,
} from './styles';

const TABS = [
    { label: 'Contacts', title: 'Contacts', subtitle: 'Influencers you have unlocked or requested contact details for' },
    { label: 'Billing Details', title: 'Billing Details', subtitle: 'Company details used on your invoices' },
    { label: 'Invoices', title: 'Invoices', subtitle: 'Your credit purchases and downloadable invoices' },
    { label: 'Change Password', title: 'Change Password', subtitle: 'You will be signed out after changing your password' },
];

// Shared input / button look for the tab forms (billing, password) so those
// components keep their logic untouched and still match the rest of the app.
const formStyles = {
    '& .MuiOutlinedInput-root': {
        background: '#F9FAFB',
        borderRadius: '10px',
        fontSize: 14,
        '& fieldset': { borderColor: '#E5E7EB' },
        '&:hover fieldset': { borderColor: '#D1D5DB' },
        '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: '1.5px' },
    },
    '& .MuiButton-contained': { ...PRIMARY_BTN_SX, py: 1.1 },
};

function TabPanel(props) {
    const { children, value, index, keepMounted = false, ...other } = props;
    return (
        <div
            role={"tabpanel"}
            hidden={value !== index}
            id={`profile_tabpanel_${index}`}
            {...other}
        >
            {(keepMounted || value === index) && children}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`
    };
}

TabPanel.propTypes = {
    children: PropTypes.node,
    keepMounted: PropTypes.bool,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
};

function Profile() {
    const [tabIndex, setTabIndex] = useState(0);
    const [userDetail, setuserDetail] = useState(null);

    const [cookies] = useCookies(["user"]);

    const handleChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    useEffect(() => {
        fetchUserDetails();
    }, []);

    const fetchUserDetails = async () => {
        if (cookies.user) {
            const response = await usersEndpoints.getUserDetails({});
            if (response['success'] && response['success'] === true) {
                let profileDetails = response['user'];
                let user = {
                    name: profileDetails['fullName'],
                    email: profileDetails['email'],
                    phone: profileDetails['phoneNumber'] ? `+91 ${profileDetails['phoneNumber']}` : '',
                    designation: '',
                    credits: response['credits'],
                    address: response['agency']['address'],
                    zipcode: response['agency']['postalCode'],
                    state: response['agency']['state'],
                    city: response['agency']['city'],
                    country: response['agency']['country'],
                    tax_id: response['agency']['gst'],
                    domainName: response['agency']['name'],
                    companyName: response['agency']['companyName'],
                    websiteLink: response['agency']['website'],
                    agency_id: response['user']['agency'],
                    emailStatus: response['user']['isEmailVerified'] ? response['user']['isEmailVerified'] : false,
                    phoneStatus: response['user']['isVerified'],
                    mailSendStatus: response['user']['isMailSent'] ? response['user']['isMailSent'] : false,
                    image: profileDetails['image'] || ""
                };
                setuserDetail(user);
            }
        }
    }

    const activeTab = TABS[tabIndex];

    return (
        <Box sx={PAGE_BG}>
          <ThemeProvider theme={theme}>
          <Container maxWidth="xl" sx={CONTAINER_SX}>
            {/* Header — same badge + gradient title as the other feature pages */}
            <Box sx={{ mb: 3.5 }}>
                <Box
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.6,
                        bgcolor: 'rgba(255,82,0,0.08)',
                        border: '1px solid rgba(255,82,0,0.15)',
                        borderRadius: '20px',
                        px: 1.25, py: 0.35, mb: 1,
                    }}
                >
                    <PersonOutlineIcon sx={{ fontSize: 12, color: BRAND }} />
                    <Typography sx={{ fontWeight: 800, fontSize: 10, color: BRAND, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                        Account
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: 28, sm: 34, md: 38 },
                        lineHeight: 1.05, mb: 0.75,
                        background: `linear-gradient(120deg, ${TEXT} 0%, #374151 55%, ${BRAND} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}
                >
                    My Profile
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: 11, sm: 13 }, color: MUTED }}>
                    Manage your account, billing information and security
                </Typography>
            </Box>

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4} lg={3.5}>
                    <UserDetailsCard details={userDetail} />
                </Grid>

                <Grid item xs={12} md={8} lg={8.5}>
                    <Tabs
                        value={tabIndex}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{
                            mb: 2.5,
                            minHeight: 0,
                            display: 'inline-flex',
                            maxWidth: '100%',
                            bgcolor: '#fff',
                            border: '1px solid rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            p: 0.5,
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTabs-flexContainer': { gap: 0.5 },
                            // Same segmented control as the Leaderboard period toggle.
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: { xs: 12, sm: 13 },
                                borderRadius: '6px',
                                minHeight: 34,
                                px: { xs: 1.5, sm: 2.25 },
                                color: '#6B7280',
                                transition: 'background-color 0.18s ease, color 0.18s ease',
                                '&:hover:not(.Mui-selected)': { bgcolor: 'rgba(255,82,0,0.06)' },
                            },
                            '& .MuiTab-root.Mui-selected': { bgcolor: BRAND, color: '#fff' },
                        }}
                    >
                        {TABS.map((t, index) => (
                            <Tab key={t.label} label={t.label} disableRipple {...a11yProps(index)} />
                        ))}
                        {/* <Tab label="Subscription" { ...a11yProps(1) } /> */}
                    </Tabs>

                    <Paper elevation={0} sx={CARD_SX}>
                        <Box sx={{ px: 3, py: 2.25, borderBottom: `1px solid ${DIVIDER}` }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 16, color: TEXT }}>
                                {activeTab.title}
                            </Typography>
                            <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: 12.5, mt: 0.25 }}>
                                {activeTab.subtitle}
                            </Typography>
                        </Box>

                        <Box sx={{ p: 3, ...formStyles }}>
                            {/* Kept mounted so switching tabs doesn't refetch the lists or reset sub-tab/search. */}
                            <TabPanel value={tabIndex} index={0} keepMounted>
                                <RequestedContactsList
                                    onCreditsChange={(credits) => setuserDetail((u) => (u ? { ...u, credits } : u))}
                                />
                            </TabPanel>
                            {/* <TabPanel value={ tabIndex } index={ 1 }>
                                    <UserSubscriptionDetails/>
                                </TabPanel> */}
                            <TabPanel value={tabIndex} index={1}>
                                {userDetail && <UserBillingDetails details={userDetail} />}
                            </TabPanel>
                            <TabPanel value={tabIndex} index={2}>
                                <UserInvoicesList />
                            </TabPanel>
                            <TabPanel value={tabIndex} index={3}>
                                <ChangePasswordComponent />
                            </TabPanel>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
          </Container>
          </ThemeProvider>
        </Box>
    );
}

export default Profile;
